import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import { hasPermissionAction } from "../../Utils/organizationPermissions";

const FINAL_ACTIONS = ["No Action", "Change Status", "Move To Nurture", "Close", "Reassign"];

const EMPTY_STEP = () => ({
  step_no: "",
  day_offset: 0,
  minute_offset: "",
  step_name: "",
  is_active: true,
});

const DEFAULT_STEPS = [
  { step_no: 1, day_offset: 0, minute_offset: 10, step_name: "First Call", is_active: true },
  { step_no: 2, day_offset: 0, minute_offset: 240, step_name: "Same Day Follow-up", is_active: true },
  { step_no: 3, day_offset: 1, minute_offset: "", step_name: "Day 1", is_active: true },
  { step_no: 4, day_offset: 3, minute_offset: "", step_name: "Day 3", is_active: true },
  { step_no: 5, day_offset: 7, minute_offset: "", step_name: "Day 7", is_active: true },
  { step_no: 6, day_offset: 10, minute_offset: "", step_name: "Day 10", is_active: true },
  { step_no: 7, day_offset: 15, minute_offset: "", step_name: "Day 15", is_active: true },
  { step_no: 8, day_offset: 21, minute_offset: "", step_name: "Day 21", is_active: true },
  { step_no: 9, day_offset: 31, minute_offset: "", step_name: "Day 31", is_active: true },
  { step_no: 10, day_offset: 45, minute_offset: "", step_name: "Day 45", is_active: true },
];

const emptyForm = () => ({
  structure_name: "",
  module_type: "Lead",
  description: "",
  is_default: false,
  final_action: "Move To Nurture",
  final_status_id: "",
  status: "Active",
  steps: DEFAULT_STEPS.map((step) => ({ ...step })),
});

const cardSx = {
  border: "1px solid var(--ems-border)",
  borderRadius: 2,
  bgcolor: "var(--ems-surface)",
  color: "var(--ems-text)",
  p: 2,
};

export function FollowupStructuresList() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const organizationId = userData?.organization?.organization_id;
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const canView = hasPermissionAction(Permission, "VIEW_FOLLOWUP_STRUCTURES");
  const canAdd = hasPermissionAction(Permission, "ADD_FOLLOWUP_STRUCTURE");
  const canEdit = hasPermissionAction(Permission, "EDIT_FOLLOWUP_STRUCTURE");
  const canDelete = hasPermissionAction(Permission, "DELETE_FOLLOWUP_STRUCTURE");

  const openView = (id) => {
    if (!canView) return;
    navigate(`/followup-structures/view/${id}`);
  };

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${MAIN_URL}/api/ems/followup-structures`, {
        params: { organization_id: organizationId },
        headers: getApiHeaders(),
      })
      .then((res) => {
        if (!cancelled) setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err?.response?.data?.message || "Failed to load follow-up structures.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const removeStructure = async (id) => {
    if (!canDelete) return;
    if (!window.confirm("Delete this follow-up structure?")) return;
    try {
      await axios.delete(`${MAIN_URL}/api/ems/followup-structures/${id}`, { headers: getApiHeaders() });
      setRows((current) => current.filter((row) => row.organization_followup_structure_id !== id));
      toast.success("Follow-up structure deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete follow-up structure.");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Follow-up Structures
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When each follow-up is due, and what happens when the steps are finished.
          </Typography>
        </Box>
        {canAdd ? (
          <Button variant="contained" onClick={() => navigate("/followup-structures/add")} sx={{ textTransform: "none" }}>
            Add Structure
          </Button>
        ) : null}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={32} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={cardSx}>
          <Typography variant="body2" color="text.secondary">
            No follow-up structure yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {rows.map((row) => (
            <Box
              key={row.organization_followup_structure_id}
              onClick={() => openView(row.organization_followup_structure_id)}
              sx={{
                ...cardSx,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                gap: 1.5,
                alignItems: { xs: "stretch", sm: "center" },
                cursor: canView ? "pointer" : "default",
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography fontWeight={700} sx={{ color: "var(--ems-text)", wordBreak: "break-word" }}>
                  {row.structure_name}
                </Typography>
                <Typography variant="body2" sx={{ color: "var(--ems-muted)" }}>
                  {row.module_type} · {row.final_action} · {row.status}
                  {row.is_default ? " · Default" : ""} · {row.steps?.length || 0} steps
                </Typography>
              </Box>
              {(canView || canEdit || canDelete) ? (
                <Box
                  onClick={(event) => event.stopPropagation()}
                  sx={{ display: "flex", gap: 1, alignSelf: { xs: "flex-end", sm: "center" }, flexShrink: 0 }}
                >
                  {canView ? (
                    <Button
                      variant="outlined"
                      onClick={() => openView(row.organization_followup_structure_id)}
                      sx={{ textTransform: "none" }}
                    >
                      View
                    </Button>
                  ) : null}
                  {canEdit ? (
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/followup-structures/edit/${row.organization_followup_structure_id}`)}
                      sx={{ textTransform: "none" }}
                    >
                      Edit
                    </Button>
                  ) : null}
                  {canDelete ? (
                    <Button
                      color="error"
                      onClick={() => removeStructure(row.organization_followup_structure_id)}
                      sx={{ textTransform: "none" }}
                    >
                      Delete
                    </Button>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function FollowupStructureForm({ readOnly = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const organizationId = userData?.organization?.organization_id;
  const canSave =
    !readOnly &&
    hasPermissionAction(
      Permission,
      isEdit ? "EDIT_FOLLOWUP_STRUCTURE" : "ADD_FOLLOWUP_STRUCTURE"
    );
  const locked = readOnly || !canSave;
  const [form, setForm] = useState(emptyForm);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    axios
      .get(`${MAIN_URL}/api/ems/lead-stages`, {
        params: { organization_id: organizationId, per_page: "all" },
        headers: getApiHeaders(),
      })
      .then((res) => {
        const payload = res.data?.data;
        const list = Array.isArray(payload) ? payload : payload?.data || [];
        setStages(list);
      })
      .catch(() => setStages([]));
  }, [organizationId]);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    axios
      .get(`${MAIN_URL}/api/ems/followup-structures/${id}`, { headers: getApiHeaders() })
      .then((res) => {
        if (cancelled) return;
        const row = res.data?.data;
        if (!row) return;
        setForm({
          structure_name: row.structure_name || "",
          module_type: row.module_type || "Lead",
          description: row.description || "",
          is_default: !!row.is_default,
          final_action: row.final_action || "No Action",
          final_status_id: row.final_status_id ? String(row.final_status_id) : "",
          status: row.status || "Active",
          steps: (row.steps || []).map((step) => ({
            step_no: step.step_no,
            day_offset: step.day_offset ?? 0,
            minute_offset: step.minute_offset ?? "",
            step_name: step.step_name || "",
            is_active: step.is_active !== false && step.is_active !== 0,
          })),
        });
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to load structure."))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const setStep = (index, name, value) => {
    setForm((prev) => {
      const steps = prev.steps.map((step, i) => (i === index ? { ...step, [name]: value } : step));
      return { ...prev, steps };
    });
  };

  const save = async () => {
    if (!canSave) return;
    if (!organizationId) {
      toast.error("No active organization selected.");
      return;
    }
    if (!String(form.structure_name).trim()) {
      toast.error("Structure name is required.");
      return;
    }
    const stepNos = form.steps.map((step) => Number(step.step_no));
    if (form.steps.length === 0 || stepNos.some((n) => !n || n < 1)) {
      toast.error("Each step needs a step number.");
      return;
    }
    if (new Set(stepNos).size !== stepNos.length) {
      toast.error("Step numbers must be unique.");
      return;
    }

    const payload = {
      organization_id: Number(organizationId),
      structure_name: form.structure_name.trim(),
      module_type: form.module_type,
      description: form.description || null,
      is_default: !!form.is_default,
      final_action: form.final_action,
      final_status_id: form.final_status_id ? Number(form.final_status_id) : null,
      status: form.status,
      steps: form.steps.map((step) => ({
        step_no: Number(step.step_no),
        day_offset: Number(step.day_offset) || 0,
        minute_offset: step.minute_offset === "" || step.minute_offset === null ? null : Number(step.minute_offset),
        step_name: step.step_name || null,
        is_active: !!step.is_active,
      })),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${MAIN_URL}/api/ems/followup-structures/${id}`, payload, { headers: getApiHeaders() });
        toast.success("Follow-up structure updated.");
      } else {
        await axios.post(`${MAIN_URL}/api/ems/followup-structures`, payload, { headers: getApiHeaders() });
        toast.success("Follow-up structure added.");
      }
      navigate("/followup-structures");
    } catch (err) {
      const firstError = err?.response?.data?.errors
        ? Object.values(err.response.data.errors)?.[0]
        : null;
      toast.error(
        (Array.isArray(firstError) ? firstError[0] : firstError) ||
          err?.response?.data?.message ||
          "Failed to save follow-up structure."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const fieldRow = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
    gap: 2,
  };
  const stepGrid = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr 1fr",
      md: canSave ? "88px minmax(0, 1.4fr) 120px 120px 88px" : "88px minmax(0, 1.4fr) 120px 120px",
    },
    gap: 1.5,
    alignItems: "center",
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", maxWidth: 980 }}>
      <Typography variant="h5" fontWeight={700} sx={{ color: "var(--ems-text)" }}>
        Follow-up Structure
      </Typography>
      <Typography variant="body2" sx={{ color: "var(--ems-muted)", mt: 0.5, mb: 2.5, maxWidth: 640 }}>
        {readOnly
          ? "View only. The follow-up plan and its steps cannot be changed."
          : "Configure the follow-up plan, what happens when every step is finished, and when each step is due."}
      </Typography>

      <Box
        sx={{
          bgcolor: "var(--ems-surface)",
          color: "var(--ems-text)",
          borderRadius: "16px",
          border: "1px solid var(--ems-border)",
          boxShadow: "var(--ems-shadow-sm)",
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Plan</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={fieldRow}>
                <TextField
                  fullWidth
                  size="small"
                  label="Structure Name"
                  value={form.structure_name}
                  onChange={(e) => setField("structure_name", e.target.value)}
                  disabled={locked}
                />
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Module"
                  value={form.module_type}
                  onChange={(e) => setField("module_type", e.target.value)}
                  disabled={locked}
                >
                  <MenuItem value="Lead">Lead</MenuItem>
                  <MenuItem value="Fee">Fee</MenuItem>
                </TextField>
              </Box>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                disabled={locked}
                multiline
                minRows={2}
              />
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>When every step is finished</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={fieldRow}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Final Action"
                  value={form.final_action}
                  onChange={(e) => setField("final_action", e.target.value)}
                  disabled={locked}
                >
                  {FINAL_ACTIONS.map((action) => (
                    <MenuItem key={action} value={action}>
                      {action}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Final Status"
                  value={form.final_status_id}
                  onChange={(e) => setField("final_status_id", e.target.value)}
                  disabled={locked}
                >
                  <MenuItem value="">None</MenuItem>
                  {stages.map((stage) => (
                    <MenuItem
                      key={stage.organization_ems_lead_stage_id}
                      value={String(stage.organization_ems_lead_stage_id)}
                    >
                      {stage.lead_stage_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={fieldRow}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Status"
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                  disabled={locked}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
                <Box sx={{ minHeight: 40, display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        checked={form.is_default}
                        onChange={(e) => setField("is_default", e.target.checked)}
                        disabled={locked}
                      />
                    }
                    label="Default structure"
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700 }}>Steps</Typography>
              {canSave ? (
                <Button
                  size="small"
                  onClick={() => setForm((prev) => ({ ...prev, steps: [...prev.steps, EMPTY_STEP()] }))}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Add step
                </Button>
              ) : null}
            </Box>
            <Typography variant="body2" sx={{ color: "var(--ems-muted)", mb: 2 }}>
              Day offset and minutes decide when the next follow-up is due.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {form.steps.map((step, index) => (
                <Box key={`${index}-${step.step_no}`} sx={stepGrid}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Step"
                    type="number"
                    value={step.step_no}
                    onChange={(e) => setStep(index, "step_no", e.target.value)}
                    disabled={locked}
                    inputProps={{ min: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Step name"
                    value={step.step_name}
                    onChange={(e) => setStep(index, "step_name", e.target.value)}
                    disabled={locked}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Day offset"
                    type="number"
                    value={step.day_offset}
                    onChange={(e) => setStep(index, "day_offset", e.target.value)}
                    disabled={locked}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Minutes"
                    type="number"
                    value={step.minute_offset}
                    onChange={(e) => setStep(index, "minute_offset", e.target.value)}
                    disabled={locked}
                  />
                  {canSave ? (
                    <Button
                      color="inherit"
                      onClick={() =>
                        setForm((prev) => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index) }))
                      }
                      sx={{ textTransform: "none", minWidth: 0 }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Divider />
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={() => navigate("/followup-structures")} disabled={saving} sx={{ textTransform: "none" }}>
            {readOnly ? "Back" : "Cancel"}
          </Button>
          {canSave ? (
            <Button
              variant="contained"
              color="primary"
              onClick={save}
              disabled={saving}
              sx={{ textTransform: "none", fontWeight: 650 }}
            >
              {saving ? "Saving…" : "Save Settings"}
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
